import { createContext, useEffect, useState } from 'react';
import { Caby, CabysResponse, Group, Menu, MenuResponse, Merchant, MerchantsAndGroupsResponse, MerchantTypeResponse, Product, ProductResponse, MenuQRResponse, TaxCodeResponse, Type, Units, UnitsResponse, OrderResponse, InvoiceExtResponse, Invoice, Table, Order } from '../../models/interfaces';
import topApi from '../../components/api/ToPApi';
import ModifierModal from '../../pages/menu-qr/ModifierModal';
import { appendNote, hasModifiers, QrModifierOption, QrProduct } from '../../pages/menu-qr/modifierChoice';




// Resultado del pago desde el menú QR (POST /card/paymentMenuQR)
export type MenuQrPaymentResult = {
    status: 'APPROVED' | 'CONFIRMED' | 'FAIL';
    orderNumber?: number;
    amount?: number;
    message?: string;
    // Desafío 3-D Secure: formulario que se envía al banco
    clientId?: string;
    action?: string;
    creq?: string;
    threeDSSessionData?: string;
};

export type MenuQrCard = { card: string; expiration: string; cvv: string; name: string };

// Carrito guardado mientras el cliente pasa por el 3-D Secure (el navegador sale de la app)
const PENDING_CART_KEY = 'menuqr_pending_cart';

export const savePendingCart = (token: string, products: Product[], detail: string) => {
    try { sessionStorage.setItem(PENDING_CART_KEY, JSON.stringify({ token, products, detail })); } catch { /* sin almacenamiento */ }
};

export const readPendingCart = (): { token: string; products: Product[]; detail: string } | null => {
    try { return JSON.parse(sessionStorage.getItem(PENDING_CART_KEY) ?? 'null'); } catch { return null; }
};

export const clearPendingCart = () => {
    try { sessionStorage.removeItem(PENDING_CART_KEY); } catch { /* sin almacenamiento */ }
};

type MenuQRContextProps = {
    isLoading: boolean;
    products: Menu[];
    selectedProducts: Product[];
    merchant: Merchant;
    table: Table;
    order: Order;
    token: String;
    loadMenuQR: (token: string) => Promise<void>;
    getMenus: () => Promise<Menu[]>;
    getProductById: (id: number) => Promise<Product>;
    getMenusAndSubmenus: () => Promise<Menu[]>;
    addProduct: (product: Product) => void;
    removeProduct: (product: Product) => void;
    createOrder: (details: string) => Promise<string>;
    clearProduct: () => void;
    cancelOrder: () => Promise<string>;
    // Pago con tarjeta (comercios con menuQrOrderOnly = false): la orden se crea al aprobarse
    payOrder: (card: MenuQrCard, detail: string) => Promise<MenuQrPaymentResult>;
    getPaymentResult: (clientId: string) => Promise<MenuQrPaymentResult>;


}

export const MenuQRContext = createContext({} as MenuQRContextProps);
export const TransactionsContext = createContext({} as MenuQRContextProps);

export const MenuQRProvider = ({ children }: any) => {

    const [products, setMenuQR] = useState<Menu[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
    const [token, setToken] = useState("");
    const [order, setOrder] = useState<Order>(null);
    const [merchant, setMerchant] = useState<Merchant>(null);
    const [table, setTable] = useState<Table>(null);





    const loadMenuQR = async (token) => {
        try {

            setIsLoading(true)
            const resp = await topApi.get<MenuQRResponse>('/menu/getMenuQR/' + token);
            if (resp.status == 200) {
                setMenuQR(resp.data.menuqr);
                setToken(token);
                setMerchant(resp.data.merchant);
                setTable(resp.data.table);

                // Si el cliente vuelve de un pago no completado, se recupera su pedido
                const pending = readPendingCart();
                if (pending && pending.token === token && pending.products?.length > 0) {
                    setSelectedProducts(pending.products);
                }
                clearPendingCart();
            }
            setIsLoading(false)
        } catch (error) {
            setIsLoading(false)
        }

    }

    const getProductById = async (productId: number) => {
        try {
            for (const menuItem of products) {
                // Buscar en productos de primer nivel
                const product = menuItem.products.find(p => p.id == productId);
                if (product) return product;

                // Buscar en submenús
                for (const subMenu of menuItem.submenu) {
                    const subProduct = subMenu.products.find(p => p.id == productId);
                    if (subProduct) return subProduct;
                }
            }
            return null;
        } catch (error) {
            setIsLoading(false)
        }

    }

    // Producto que espera la elección de sus modificadores
    const [askingModifiers, setAskingModifiers] = useState<QrProduct | null>(null);

    // Suma una unidad; las opciones elegidas se anotan en la línea
    const addLine = (product: Product, options: QrModifierOption[] = []) => {
        setSelectedProducts(prev => {
            const existingProduct = prev.find(p => p.id === product.id) as QrProduct | undefined;

            if (existingProduct) {
                return prev.map(p =>
                    p.id === product.id
                        ? { ...p, quantity: (p.quantity ?? 1) + 1, message: appendNote(existingProduct.message, options) } as QrProduct // si no tiene quantity previa, parte de 1
                        : p
                );
            } else {
                return [...prev, { ...product, quantity: 1, message: appendNote(undefined, options) } as QrProduct];
            }
        });
    };

    const addProduct = (product: Product) => {
        if (hasModifiers(product as QrProduct)) {
            setAskingModifiers(product as QrProduct);
            return;
        }
        addLine(product);
    };

    // Producto con sus opciones; los extras con precio se agregan como líneas de su producto
    const confirmModifiers = async (options: QrModifierOption[]) => {
        const product = askingModifiers!;
        setAskingModifiers(null);
        addLine(product, options);
        for (const option of options.filter(o => o.productId)) {
            const extra = await getProductById(option.productId!);
            if (extra) addLine(extra);
        }
    };

    const removeProduct = (product: Product) => {
        setSelectedProducts(prev => {
            const existingProduct = prev.find(p => p.id === product.id);

            if (!existingProduct) return prev; // no existe, no cambia nada

            if ((existingProduct.quantity ?? 1) <= 1) {
                // eliminar del array
                return prev.filter(p => p.id !== product.id);
            } else {
                // reducir cantidad
                return prev.map(p =>
                    p.id === product.id
                        ? { ...p, quantity: (p.quantity ?? 1) - 1 }
                        : p
                );
            }
        });
    };

    const getMenus = async () => {
        return products.filter((menu) => menu.parentId == null);
    }

    const getMenusAndSubmenus = async () => {
        return flattenMenus(products);
    }

    const flattenMenus = (menus: Menu[]): Menu[] => {
        let result: Menu[] = [];

        menus.forEach((menu) => {
            // Agregar el menú actual
            result.push(menu);

            // Si tiene submenús, llamar recursivamente y agregar los resultados
            if (menu.submenu && menu.submenu.length > 0) {
                result = result.concat(flattenMenus(menu.submenu));
            }
        });

        return result;
    };

    const clearProduct = async () => {
        setSelectedProducts([]);
    }

    const createOrder = async (detail: string) => {
        try {

            setIsLoading(true)

            const currentProducts = selectedProducts.map((product) => ({
                id: product.id,
                quantity: product.quantity,
                price: product.price,
                message: (product as QrProduct).message ?? ""
            }));

            const order = {
                detail,
                products: currentProducts,
                takeout: false
            }

            let resp = await topApi.post<OrderResponse>(`/order/external`, { order, token });

            if (resp.status == 200) {

                setOrder(resp.data.order);
                setIsLoading(false)
                return "success";


            }
            setIsLoading(false)
            return "Ocurrió un error de comunicación, intente de nuevo.";
        } catch (e) {
            setIsLoading(false)
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }

    const payOrder = async (card: MenuQrCard, detail: string): Promise<MenuQrPaymentResult> => {
        try {
            const order = {
                detail,
                products: selectedProducts.map((product) => ({ id: product.id, quantity: product.quantity, message: (product as QrProduct).message ?? "" })),
            };
            const resp = await topApi.post<MenuQrPaymentResult>('/card/paymentMenuQR', {
                token,
                card,
                order,
                page_result: `${window.location.origin}/menuqr/result`,
            });
            return resp.data;
        } catch (e) {
            return { status: 'FAIL', message: e.response?.data?.error || "Ocurrió un error de comunicación, intente de nuevo." };
        }
    }

    // Resultado del pago al volver del 3-D Secure
    const getPaymentResult = async (clientId: string): Promise<MenuQrPaymentResult> => {
        try {
            const resp = await topApi.get<any>(`/card/getResult3ds/${encodeURIComponent(clientId)}`);
            const result = resp.data?.payResponse?.payResponse ?? {};
            if (result.status === 'APPROVED') {
                return { status: 'APPROVED', orderNumber: result.orderNumber, message: result.orderError };
            }
            return { status: 'FAIL', message: result.errMsg || 'El pago no fue aprobado.' };
        } catch (e) {
            return { status: 'FAIL', message: e.response?.data?.error || 'No se pudo consultar el resultado del pago.' };
        }
    }

    const cancelOrder = async () => {
        try {

            setIsLoading(true)


            let resp = await topApi.put(`/order/cancel/external/${order.id}/${token}`, { order, token });

            if (resp.status == 200) {

                setIsLoading(false)
                return "success";


            }
            setIsLoading(false)
            return "Ocurrió un error de comunicación, intente de nuevo.";
        } catch (e) {
            setIsLoading(false)
            const error = e.response?.data?.error;
            return error || "Ocurrió un error de comunicación, intente de nuevo.";
        }
    }






    return (
        <MenuQRContext.Provider value={{
            isLoading,
            products,
            selectedProducts,
            merchant,
            table,
            order,
            token, clearProduct,
            addProduct,
            removeProduct,
            loadMenuQR,
            getProductById,
            getMenus,
            getMenusAndSubmenus,
            createOrder,
            cancelOrder,
            payOrder,
            getPaymentResult

        }}>
            {children}
            {askingModifiers && (
                <ModifierModal product={askingModifiers} onCancel={() => setAskingModifiers(null)} onConfirm={confirmModifiers} />
            )}
        </MenuQRContext.Provider>
    );
}